/**
 * Iter XX.87 — Clarity session tags, UTM persistence, and one-shot
 * dedup helpers for the Instagram-to-activation funnel.
 *
 * Founder rules (hard):
 *   · NEVER send email / name / chat text / scorecard content /
 *     session_id / user_id verbatim to Clarity in tags or event
 *     metadata. Tags are enum-ish scalars only.
 *   · Every impression / view event is deduplicated per session.
 *   · UTM parameters survive SPA route transitions so Clarity can
 *     bucket landing→chat→first_message_sent per campaign.
 *
 * All helpers are safe to call even if the Clarity tag is blocked
 * (ad-blocker), missing (no project id), or the browser doesn't
 * support IntersectionObserver.
 */
import { set, track } from "./clarity";

const UTM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign",
  "utm_content", "utm_term", "gclid", "fbclid",
];
const UTM_SS_KEY = "gully_utm_v1";
const DEDUP_SS_PREFIX = "gully_evt_once_v1:";

// ---------------------------------------------------------------------
// UTM capture + persistence (survives SPA nav; cleared with the tab)
// ---------------------------------------------------------------------
export function captureUtmsFromUrl(search) {
  try {
    const q = new URLSearchParams(search || window.location.search || "");
    const hits = {};
    for (const k of UTM_KEYS) {
      const v = q.get(k);
      if (v) hits[k] = String(v).slice(0, 96);
    }
    if (Object.keys(hits).length === 0) return null;
    // Merge with any earlier capture so second-nav UTMs don't wipe.
    let existing = {};
    try {
      existing = JSON.parse(sessionStorage.getItem(UTM_SS_KEY) || "{}");
    } catch { /* noop */ }
    const merged = { ...existing, ...hits };
    sessionStorage.setItem(UTM_SS_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return null;
  }
}

export function getStoredUtms() {
  try { return JSON.parse(sessionStorage.getItem(UTM_SS_KEY) || "{}"); }
  catch { return {}; }
}

// ---------------------------------------------------------------------
// Dedup — sessionStorage flag; safe under React strict-mode.
// ---------------------------------------------------------------------
export function alreadyFired(eventKey) {
  try { return sessionStorage.getItem(DEDUP_SS_PREFIX + eventKey) === "1"; }
  catch { return false; }
}

export function markFired(eventKey) {
  try { sessionStorage.setItem(DEDUP_SS_PREFIX + eventKey, "1"); }
  catch { /* noop */ }
}

/**
 * Fire a Clarity event AT MOST ONCE per browser session, keyed by
 * `dedupKey` (defaults to the event name).
 */
export function trackOnce(name, meta, dedupKey) {
  const key = dedupKey || name;
  if (alreadyFired(key)) return false;
  markFired(key);
  track(name, meta);
  return true;
}

// ---------------------------------------------------------------------
// Session-level tags. Called from ClarityProvider on boot + on
// auth change. Any missing value is silently omitted (never sent
// as "undefined" / empty string).
// ---------------------------------------------------------------------
function _detectVisitorType() {
  try {
    const seen = localStorage.getItem("gully_returning_v1");
    if (seen === "1") return "returning";
    localStorage.setItem("gully_returning_v1", "1");
    return "new";
  } catch {
    return "new";
  }
}

function _detectDeviceExperience() {
  try {
    // Viewport + touch-pointer signals FIRST — they correctly classify
    // Instagram in-app WebViews and mobile emulation that share a
    // desktop-shaped UA string. Fall back to UA sniffing when neither
    // signal is available (headless SSR).
    const w = (window.innerWidth || document.documentElement?.clientWidth || 0);
    if (w > 0 && w <= 820) return "mobile";
    if (typeof window.matchMedia === "function"
        && window.matchMedia("(pointer: coarse)").matches) {
      return "mobile";
    }
    const ua = (navigator.userAgent || "").toLowerCase();
    const mobileRe = /mobile|android|iphone|ipad|ipod|tablet|kindle|opera mini/;
    return mobileRe.test(ua) ? "mobile" : "desktop";
  } catch {
    return "desktop";
  }
}

function _detectEnvironment() {
  try {
    const host = (window.location.hostname || "").toLowerCase();
    if (host === "askgully.com" || host.endsWith(".askgully.com")) return "production";
    if (host === "localhost" || host === "127.0.0.1") return "local";
    return "preview";
  } catch {
    return "preview";
  }
}

function _detectInternalUser() {
  try {
    const url = new URLSearchParams(window.location.search || "");
    if (url.get("internal") === "1" || url.get("internal") === "true") return "true";
    if (localStorage.getItem("gully_internal") === "1") return "true";
    // Emergent founder override params seen elsewhere in the app.
    if (["activation", "r3"].includes(url.get("internal") || "")) return "true";
    return "false";
  } catch { return "false"; }
}

function _detectTrafficSource(utms) {
  const src = (utms || {}).utm_source;
  if (src) return String(src).toLowerCase();
  try {
    const ref = (document.referrer || "").toLowerCase();
    if (!ref) return "direct";
    if (ref.includes("instagram.com")) return "instagram";
    if (ref.includes("facebook.com")) return "facebook";
    if (ref.includes("t.co") || ref.includes("twitter.com") || ref.includes("x.com")) return "twitter";
    if (ref.includes("google.")) return "google";
    if (ref.includes("bing.")) return "bing";
    if (ref.includes("linkedin.")) return "linkedin";
    if (ref.includes("youtube.")) return "youtube";
    if (ref.includes("askgully.com")) return "internal";
    return "referral";
  } catch { return "direct"; }
}

let _sessionTagsBootedFor = { visitor: null, auth: null };

/**
 * Apply all session-level Clarity custom tags. Idempotent — safe to
 * call from ClarityProvider on boot AND on auth/route changes.
 *
 * NEVER touches PII. Tag values are all enum-ish scalars.
 */
// Iter XX.103 → XX.110 — Default landing variant is `demo_v1` (the
// live coaching demonstration landing shipped in XX.110, 100% rollout).
// Callers that serve a different landing MUST pass `landingVariant`
// explicitly. Bumping this default alongside the served landing keeps
// the Clarity session tag in sync with the DOM the visitor actually
// saw, which is what dashboards filter on.
export function applySessionTags({ authState = "anonymous", landingVariant = "demo_v1" } = {}) {
  const utms = { ...getStoredUtms(), ...(captureUtmsFromUrl() || {}) };
  const visitorType = _detectVisitorType();
  const deviceExperience = _detectDeviceExperience();
  const environment = _detectEnvironment();
  const internalUser = _detectInternalUser();
  const trafficSource = _detectTrafficSource(utms);

  // Signature to short-circuit repeat calls with unchanged values.
  const sig = `${authState}|${visitorType}|${deviceExperience}|${environment}|${internalUser}|${trafficSource}|${landingVariant}`;
  if (_sessionTagsBootedFor.visitor === sig) return;
  _sessionTagsBootedFor.visitor = sig;

  set("traffic_source", trafficSource);
  set("visitor_type", visitorType);
  set("auth_state", authState);
  set("device_experience", deviceExperience);
  set("landing_variant", landingVariant);
  set("internal_user", internalUser);
  set("environment", environment);
  for (const k of UTM_KEYS) {
    if (utms[k]) set(k, utms[k]);
  }
}

// ---------------------------------------------------------------------
// IntersectionObserver-based "≥50% visible" impression helper.
// Fires `callback` at most once when the target crosses the 50%
// visibility threshold AND the tab is active.
// ---------------------------------------------------------------------
export function onceVisible(el, callback, { threshold = 0.5 } = {}) {
  if (!el || typeof IntersectionObserver === "undefined") {
    // Graceful fallback for jsdom / very old browsers: fire immediately
    // so tests + legacy clients still get one impression event.
    try { callback(); } catch { /* noop */ }
    return () => {};
  }
  let fired = false;
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (fired) return;
      if (!e.isIntersecting) continue;
      if (e.intersectionRatio < threshold) continue;
      if (document.visibilityState !== "visible") continue;
      fired = true;
      io.disconnect();
      try { callback(); } catch { /* noop */ }
    }
  }, { threshold: [threshold] });
  io.observe(el);
  return () => { try { io.disconnect(); } catch { /* noop */ } };
}

// ---------------------------------------------------------------------
// "Continuously engaged for N seconds" helper.
// Requires: element ≥ 50% visible, tab active, no navigation away.
// The timer pauses when tab hides and resumes on visibility return.
// Fires `callback` at most once.
// ---------------------------------------------------------------------
export function onceEngagedFor(el, seconds, callback, { threshold = 0.5 } = {}) {
  if (!el) { try { callback(); } catch { /* noop */ } return () => {}; }
  let elapsedMs = 0;
  let timer = null;
  let visible = false;
  let fired = false;
  const targetMs = Math.max(500, Math.round(seconds * 1000));

  function _tick() {
    elapsedMs += 200;
    if (elapsedMs >= targetMs && !fired) {
      fired = true;
      stopAll();
      try { callback(); } catch { /* noop */ }
    }
  }
  function _startTimer() {
    if (timer) return;
    if (!visible) return;
    if (document.visibilityState !== "visible") return;
    timer = setInterval(_tick, 200);
  }
  function _stopTimer() {
    if (timer) { clearInterval(timer); timer = null; }
  }
  function stopAll() {
    _stopTimer();
    try { document.removeEventListener("visibilitychange", _onVis); }
    catch { /* noop */ }
    try { io.disconnect(); } catch { /* noop */ }
  }
  function _onVis() {
    if (document.visibilityState === "visible") _startTimer();
    else _stopTimer();
  }

  let io;
  if (typeof IntersectionObserver !== "undefined") {
    io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        visible = e.isIntersecting && e.intersectionRatio >= threshold;
        if (visible) _startTimer(); else _stopTimer();
      }
    }, { threshold: [threshold] });
    io.observe(el);
  } else {
    // No IO — assume visible.
    visible = true;
    _startTimer();
    io = { disconnect() {} };
  }
  document.addEventListener("visibilitychange", _onVis);
  return stopAll;
}
